import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays, startOfMonth, endOfMonth, setHours, setMinutes, isToday, startOfDay, endOfDay, isBefore, isAfter, compareAsc } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { scheduleAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, CheckCircle2, Clock, MapPin, CircleDot, Phone, Users, Mail, Footprints, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { handleError } from '../lib/errorHandler';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/calendar.css';

// Setup date-fns localizer
const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Event type colors matching requirements
const eventTypeColors = {
  event: '#3B82F6',      // Blue
  task: '#EAB308',       // Yellow
  follow_up: '#22C55E',  // Green
  visit: '#F97316',      // Orange
  meeting: '#EC4899',    // Pink
  call: '#06B6D4',       // Cyan
};

const eventTypeLabels = {
  event: 'Event',
  task: 'Task',
  follow_up: 'Follow-up',
  visit: 'Visit',
  meeting: 'Meeting',
  call: 'Call',
};

const eventTypeIcons = {
  event: CalendarIcon,
  task: CheckCircle2,
  follow_up: Mail,
  visit: Footprints,
  meeting: Users,
  call: Phone,
};

const priorityOrder = { high: 0, medium: 1, low: 2 };
const statusOrder = { pending: 0, scheduled: 1, upcoming: 2, completed: 3, cancelled: 4, missed: 5 };

export default function ScheduleCalendar() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // month, week, day
  
  // Create/Edit sheet states
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'event',
    start_time: '',
    end_time: '',
    location: '',
    priority: 'medium',
    reminder_minutes: null,
    status: 'pending',
  });

  // Notification permission state
  const [notificationPermission, setNotificationPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  // Request notification permission on mount
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {
        setNotificationPermission(permission);
      });
    }
  }, []);

  // Check for reminders every minute
  useEffect(() => {
    const checkReminders = () => {
      const now = new Date();
      
      events.forEach(event => {
        const eventData = event.resource;
        
        // Skip if no reminder, is lead activity (read-only), or not pending/scheduled
        if (!eventData.reminder_minutes || 
            eventData.is_lead_activity || 
            !['pending', 'scheduled', 'upcoming'].includes(eventData.status)) {
          return;
        }
        
        const eventStart = new Date(eventData.start_time);
        const reminderTime = new Date(eventStart.getTime() - (eventData.reminder_minutes * 60000));
        
        // Check if reminder time is within the next minute
        const diff = reminderTime.getTime() - now.getTime();
        if (diff > 0 && diff <= 60000) {
          showNotification(eventData);
        }
      });
    };
    
    // Check immediately and then every minute
    checkReminders();
    const interval = setInterval(checkReminders, 60000);
    
    return () => clearInterval(interval);
  }, [events]);

  useEffect(() => {
    loadEvents();
  }, [currentDate, view]);

  // Show notification for event
  const showNotification = (eventData) => {
    if (notificationPermission !== 'granted') {
      toast.info(`Reminder: ${eventData.title}`, {
        description: `Starting ${format(new Date(eventData.start_time), 'h:mm a')}`,
        duration: 10000,
      });
      return;
    }
    
    const reminderText = eventData.reminder_minutes === 0 
      ? 'Starting now' 
      : `Starting in ${eventData.reminder_minutes} minutes`;
    
    new Notification(`Reminder: ${eventData.title}`, {
      body: `${reminderText}\n${eventData.description || ''}`,
      icon: '/favicon.ico',
      tag: `event-${eventData.id}`,
    });
    
    // Also show toast for in-app notification
    toast.info(`Reminder: ${eventData.title}`, {
      description: reminderText,
      duration: 10000,
    });
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      const start = startOfMonth(currentDate);
      const end = endOfMonth(addMonths(currentDate, 1)); // Load current + next month
      
      // Use calendar-events endpoint that includes lead activities
      const response = await scheduleAPI.getCalendarEvents({
        start_date: start.toISOString(),
        end_date: end.toISOString(),
      });
      
      // Transform to RBC format
      const transformedEvents = response.data.map(event => {
        const startTime = new Date(event.start_time);
        let endTime = new Date(event.end_time);
        
        // If start and end are the same (common for tasks, follow-ups, visits),
        // add 1 hour duration for better visualization in week/day views
        if (startTime.getTime() === endTime.getTime()) {
          endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // Add 1 hour
        }
        
        return {
          ...event,
          start: startTime,
          end: endTime,
          title: event.title,
          resource: event,
        };
      });
      
      setEvents(transformedEvents);
    } catch (error) {
      console.error('Failed to load events:', error);
      toast.error('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  // Navigation handlers - view aware
  const handleNavigate = (action) => {
    if (action === 'PREV') {
      setCurrentDate(prev => {
        if (view === 'month') {
          return subMonths(prev, 1);
        } else if (view === 'week') {
          return subWeeks(prev, 1);
        } else {
          return subDays(prev, 1);
        }
      });
    } else if (action === 'NEXT') {
      setCurrentDate(prev => {
        if (view === 'month') {
          return addMonths(prev, 1);
        } else if (view === 'week') {
          return addWeeks(prev, 1);
        } else {
          return addDays(prev, 1);
        }
      });
    } else if (action === 'TODAY') {
      setCurrentDate(new Date());
    }
  };

  // Select slot (date click)
  const handleSelectSlot = useCallback((slotInfo) => {
    // Get the clicked date
    const clickedDate = new Date(slotInfo.start);
    
    // For month view: start time is 7 AM, end time is 11 PM on same date
    // For week/day view: start time is clicked time, end time is 11 PM on same date
    let startDateTime, endDateTime;
    
    if (view === 'month') {
      // Month view: 7 AM to 11 PM on same day
      startDateTime = setMinutes(setHours(clickedDate, 7), 0);
      endDateTime = setMinutes(setHours(clickedDate, 23), 0);
    } else {
      // Week/Day view: use clicked time, set end to 11 PM same day
      startDateTime = clickedDate;
      // Create end time on same date at 11 PM
      endDateTime = setMinutes(setHours(new Date(clickedDate.getFullYear(), clickedDate.getMonth(), clickedDate.getDate()), 23), 0);
    }
    
    setSelectedSlot(slotInfo);
    setFormData({
      title: '',
      description: '',
      event_type: 'event',
      start_time: format(startDateTime, "yyyy-MM-dd'T'HH:mm"),
      end_time: format(endDateTime, "yyyy-MM-dd'T'HH:mm"),
      location: '',
      priority: 'medium',
      reminder_minutes: null,
      status: 'pending',
    });
    setCreateSheetOpen(true);
  }, [view]);

  // Select event (event click)
  const handleSelectEvent = useCallback((event) => {
    setSelectedEvent(event.resource);
    setFormData({
      title: event.resource.title,
      description: event.resource.description || '',
      event_type: event.resource.event_type,
      start_time: new Date(event.resource.start_time).toISOString().slice(0, 16),
      end_time: new Date(event.resource.end_time).toISOString().slice(0, 16),
      location: event.resource.location || '',
      priority: event.resource.priority,
      reminder_minutes: event.resource.reminder_minutes,
      status: event.resource.status,
    });
    setDetailSheetOpen(true);
  }, []);

  // Event style getter (color coding)
  const eventStyleGetter = useCallback((event) => {
    const baseColor = eventTypeColors[event.resource.event_type] || '#6B7280';
    
    // Use lighter opacity for week and day views
    const opacity = (view === 'week' || view === 'day') ? 0.65 : 0.9;
    
    return {
      style: {
        backgroundColor: baseColor,
        borderRadius: '4px',
        opacity: opacity,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '0.875rem',
        padding: '2px 4px',
      },
    };
  }, [view]);

  // Handle event type change
  const handleEventTypeChange = (value) => {
    setFormData({...formData, event_type: value});
  };

  // Create event
  const handleCreateEvent = async () => {
    if (!formData.title || !formData.start_time || !formData.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (new Date(formData.start_time) >= new Date(formData.end_time)) {
      toast.error('End time must be after start time');
      return;
    }

    try {
      const eventData = {
        title: formData.title,
        description: formData.description,
        event_type: formData.event_type,
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString(),
        location: formData.location,
        priority: formData.priority,
        reminder_minutes: formData.reminder_minutes ? parseInt(formData.reminder_minutes) : null,
        status: formData.status,
      };

      await scheduleAPI.createEvent(eventData);
      toast.success(`${formData.event_type === 'event' ? 'Event' : 'Task'} created successfully`);
      setCreateSheetOpen(false);
      loadEvents();
    } catch (error) {
      console.error('Failed to create event:', error);
      toast.error('Failed to create entry');
    }
  };

  // Update event
  const handleUpdateEvent = async () => {
    if (!formData.title || !formData.start_time || !formData.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (new Date(formData.start_time) >= new Date(formData.end_time)) {
      toast.error('End time must be after start time');
      return;
    }

    try {
      const eventData = {
        title: formData.title,
        description: formData.description,
        event_type: formData.event_type,
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString(),
        location: formData.location,
        priority: formData.priority,
        reminder_minutes: formData.reminder_minutes ? parseInt(formData.reminder_minutes) : null,
        status: formData.status,
      };

      await scheduleAPI.updateEvent(selectedEvent.id, eventData);
      toast.success('Event updated successfully');
      setDetailSheetOpen(false);
      loadEvents();
    } catch (error) {
      console.error('Failed to update event:', error);
      toast.error('Failed to update event');
    }
  };

  // Delete event
  const handleDeleteEvent = async () => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      await scheduleAPI.deleteEvent(selectedEvent.id);
      toast.success('Event deleted successfully');
      setDetailSheetOpen(false);
      loadEvents();
    } catch (error) {
      console.error('Failed to delete event:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete event';
      toast.error(errorMsg);
    }
  };

  // Custom event component with tooltip
  const CustomEvent = ({ event }) => {
    const eventData = event.resource;
    
    return (
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <div className="px-1 py-0.5 text-xs font-medium truncate cursor-pointer">
              {event.title}
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Badge style={{ backgroundColor: eventTypeColors[eventData.event_type] }} className="text-xs">
                  {eventTypeLabels[eventData.event_type]}
                </Badge>
                {eventData.priority && (
                  <Badge variant={eventData.priority === 'high' ? 'destructive' : 'secondary'} className="text-xs">
                    {eventData.priority}
                  </Badge>
                )}
              </div>
              <p className="font-semibold text-sm">{eventData.title}</p>
              {eventData.description && (
                <p className="text-xs text-muted-foreground line-clamp-2">{eventData.description}</p>
              )}
              <p className="text-xs">
                {format(new Date(eventData.start_time), 'MMM d, h:mm a')} - {format(new Date(eventData.end_time), 'h:mm a')}
              </p>
              {eventData.location && (
                <p className="text-xs text-muted-foreground">📍 {eventData.location}</p>
              )}
              {eventData.is_lead_activity && (
                <p className="text-xs text-blue-600 font-medium">👤 {eventData.lead_name}</p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Quick complete handler
  const handleQuickComplete = async (eventData) => {
    if (eventData.is_lead_activity) return;
    try {
      await scheduleAPI.updateEvent(eventData.id, { ...eventData, status: 'completed' });
      toast.success(`"${eventData.title}" marked complete`);
      loadEvents();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Today's events — filtered, sorted by time then priority
  const todayEvents = useMemo(() => {
    const todayStart = startOfDay(new Date());
    const todayEnd = endOfDay(new Date());
    return events
      .filter(e => {
        const start = new Date(e.resource.start_time);
        return start >= todayStart && start <= todayEnd;
      })
      .sort((a, b) => {
        const pa = priorityOrder[a.resource.priority] ?? 2;
        const pb = priorityOrder[b.resource.priority] ?? 2;
        if (pa !== pb) return pa - pb;
        return compareAsc(new Date(a.resource.start_time), new Date(b.resource.start_time));
      });
  }, [events]);

  const pendingToday = todayEvents.filter(e => !['completed', 'cancelled'].includes(e.resource.status));
  const completedToday = todayEvents.filter(e => e.resource.status === 'completed');
  const overdueToday = pendingToday.filter(e => isBefore(new Date(e.resource.end_time), new Date()));

  // Custom toolbar — compact for 60% width
  const CustomToolbar = ({ onNavigate, label }) => (
    <div className="flex items-center justify-between p-3 bg-background border-b gap-2">
      <div className="flex items-center gap-1.5">
        <Button variant="outline" size="sm" onClick={() => onNavigate('TODAY')} className="text-xs h-8">
          Today
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onNavigate('PREV')}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onNavigate('NEXT')}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <h2 className="text-base font-semibold ml-1">{label}</h2>
      </div>
      <div className="flex items-center gap-2">
        <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
          <SelectTrigger className="w-[130px] h-8 text-xs">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="event">Event</SelectItem>
            <SelectItem value="task">Task</SelectItem>
            <SelectItem value="follow_up">Follow-Up</SelectItem>
            <SelectItem value="visit">Visit</SelectItem>
            <SelectItem value="meeting">Meeting</SelectItem>
          </SelectContent>
        </Select>
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="h-8">
            <TabsTrigger value="month" className="text-xs px-2.5 h-7">Month</TabsTrigger>
            <TabsTrigger value="week" className="text-xs px-2.5 h-7">Week</TabsTrigger>
            <TabsTrigger value="day" className="text-xs px-2.5 h-7">Day</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
    </div>
  );

  // Render a single digest item row
  const DigestItem = ({ eventData }) => {
    const Icon = eventTypeIcons[eventData.event_type] || CalendarIcon;
    const color = eventTypeColors[eventData.event_type] || '#6B7280';
    const isCompleted = eventData.status === 'completed';
    const isCancelled = eventData.status === 'cancelled';
    const isOverdue = !isCompleted && !isCancelled && isBefore(new Date(eventData.end_time), new Date());

    return (
      <div
        data-testid={`digest-item-${eventData.id}`}
        onClick={() => {
          setSelectedEvent(eventData);
          setFormData({
            title: eventData.title,
            description: eventData.description || '',
            event_type: eventData.event_type,
            start_time: new Date(eventData.start_time).toISOString().slice(0, 16),
            end_time: new Date(eventData.end_time).toISOString().slice(0, 16),
            location: eventData.location || '',
            priority: eventData.priority,
            reminder_minutes: eventData.reminder_minutes,
            status: eventData.status,
          });
          setDetailSheetOpen(true);
        }}
        className={`group flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all border
          ${isCompleted ? 'opacity-50 bg-muted/30 border-transparent' : 'bg-background border-border hover:border-primary/30 hover:shadow-sm'}`}
      >
        {/* Color indicator + icon */}
        <div className="flex-shrink-0 mt-0.5 relative">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
            <Icon className="h-4 w-4" style={{ color }} />
          </div>
          {isOverdue && (
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-background" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className={`text-sm font-medium truncate ${isCompleted ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
              {eventData.title}
            </span>
            {eventData.priority === 'high' && !isCompleted && (
              <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>{format(new Date(eventData.start_time), 'h:mm a')}</span>
            {eventData.location && (
              <>
                <span className="text-border">|</span>
                <MapPin className="h-3 w-3" />
                <span className="truncate max-w-[100px]">{eventData.location}</span>
              </>
            )}
            {eventData.is_lead_activity && eventData.lead_name && (
              <>
                <span className="text-border">|</span>
                <span className="text-blue-600 dark:text-blue-400 truncate max-w-[100px]">{eventData.lead_name}</span>
              </>
            )}
          </div>
          {isOverdue && (
            <span className="text-[11px] font-medium text-red-500 mt-0.5 inline-block">Overdue</span>
          )}
        </div>

        {/* Quick action */}
        {!isCompleted && !isCancelled && !eventData.is_lead_activity && (
          <button
            data-testid={`complete-btn-${eventData.id}`}
            onClick={(e) => { e.stopPropagation(); handleQuickComplete(eventData); }}
            className="flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Mark complete"
          >
            <CheckCircle2 className="h-5 w-5 text-muted-foreground hover:text-green-600 transition-colors" />
          </button>
        )}
        {isCompleted && (
          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-1.5" />
        )}
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col" data-testid="schedule-calendar">
      {/* Split Layout: Digest (40%) | Calendar (60%) */}
      <div className="flex-1 flex min-h-0">
        {/* LEFT — Today's Digest */}
        <div className="w-[40%] border-r flex flex-col min-h-0" data-testid="today-digest-panel">
          {/* Digest Header */}
          <div className="p-4 border-b bg-background flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground" style={{ fontFamily: "var(--font-heading, 'Space Grotesk', sans-serif)" }}>
                Today's Focus
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {format(new Date(), 'EEEE, MMMM d')} — {pendingToday.length} pending{overdueToday.length > 0 && <span className="text-red-500 font-medium"> / {overdueToday.length} overdue</span>}
              </p>
            </div>
            <Button
              size="sm"
              className="h-8"
              data-testid="add-event-btn"
              onClick={() => {
                setSelectedSlot(null);
                const now = new Date();
                const startTime = setMinutes(setHours(now, 7), 0);
                const endTime = setMinutes(setHours(now, 23), 0);
                setFormData({
                  title: '', description: '', event_type: 'task',
                  start_time: startTime.toISOString().slice(0, 16),
                  end_time: endTime.toISOString().slice(0, 16),
                  location: '', priority: 'medium', reminder_minutes: null, status: 'pending',
                });
                setCreateSheetOpen(true);
              }}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />Add
            </Button>
          </div>

          {/* Summary Stat Chips */}
          <div className="px-4 py-2.5 border-b bg-muted/20 flex gap-2 flex-wrap" data-testid="digest-summary">
            {Object.entries(eventTypeLabels).map(([type, label]) => {
              const count = todayEvents.filter(e => e.resource.event_type === type).length;
              if (count === 0) return null;
              return (
                <div key={type} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-background border" style={{ borderColor: `${eventTypeColors[type]}40` }}>
                  <CircleDot className="h-2.5 w-2.5" style={{ color: eventTypeColors[type] }} />
                  {count} {label}{count > 1 ? 's' : ''}
                </div>
              );
            })}
            {todayEvents.length === 0 && (
              <span className="text-xs text-muted-foreground">No items scheduled for today</span>
            )}
          </div>

          {/* Digest Items List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="digest-items-list">
            {/* Overdue section */}
            {overdueToday.length > 0 && (
              <div className="mb-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-red-500 mb-1.5 px-1">Overdue</p>
                {overdueToday.map(e => <DigestItem key={e.resource.id} eventData={e.resource} />)}
              </div>
            )}
            {/* Pending section */}
            {pendingToday.filter(e => !isBefore(new Date(e.resource.end_time), new Date())).length > 0 && (
              <div className="mb-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 px-1">Upcoming</p>
                {pendingToday
                  .filter(e => !isBefore(new Date(e.resource.end_time), new Date()))
                  .map(e => <DigestItem key={e.resource.id} eventData={e.resource} />)}
              </div>
            )}
            {/* Completed section */}
            {completedToday.length > 0 && (
              <div className="mb-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 px-1">
                  Completed ({completedToday.length})
                </p>
                {completedToday.map(e => <DigestItem key={e.resource.id} eventData={e.resource} />)}
              </div>
            )}
            {/* Empty state */}
            {todayEvents.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <CalendarIcon className="h-10 w-10 text-muted-foreground/30 mb-3" />
                <p className="text-sm font-medium text-muted-foreground">No tasks for today</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Click "Add" to schedule something</p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Calendar View */}
        <div className="w-[60%] flex flex-col min-h-0" data-testid="calendar-panel">
          <Card className="flex-1 flex flex-col border-0 rounded-none">
            <CardContent className="flex-1 p-0">
              <Calendar
                localizer={localizer}
                events={eventTypeFilter === 'all' ? events : events.filter(e => e.resource?.event_type === eventTypeFilter)}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%' }}
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                selectable
                popup
                views={['month', 'week', 'day']}
                view={view}
                onView={setView}
                date={currentDate}
                onNavigate={setCurrentDate}
                eventPropGetter={eventStyleGetter}
                components={{
                  toolbar: (props) => <CustomToolbar {...props} onNavigate={handleNavigate} />,
                  event: CustomEvent,
                }}
                step={30}
                timeslots={2}
                min={new Date(2024, 0, 1, 7, 0, 0)}
                max={new Date(2024, 0, 1, 22, 0, 0)}
                showMultiDayTimes
                dayLayoutAlgorithm="no-overlap"
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Event Sheet */}
      <Sheet open={createSheetOpen} onOpenChange={setCreateSheetOpen}>
        <SheetContent className="overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Create New Entry</SheetTitle>
            <SheetDescription>
              Create an event, task, or schedule a follow-up
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 mt-6">
            <div>
              <Label>Entry Type *</Label>
              <Select value={formData.event_type} onValueChange={handleEventTypeChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="event">Event</SelectItem>
                  <SelectItem value="task">Task</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="follow_up">Follow-up</SelectItem>
                  <SelectItem value="visit">Visit</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Enter title"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Enter description"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                />
              </div>
              <div>
                <Label>End Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={formData.end_time}
                  onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Location</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="Enter location"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reminder</Label>
                <Select
                  value={formData.reminder_minutes?.toString() || 'none'}
                  onValueChange={(value) => setFormData({...formData, reminder_minutes: value === 'none' ? null : value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No reminder</SelectItem>
                    <SelectItem value="0">At time of event</SelectItem>
                    <SelectItem value="15">15 minutes before</SelectItem>
                    <SelectItem value="60">1 hour before</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setCreateSheetOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateEvent}>
                Create Entry
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Event Detail/Edit Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {selectedEvent?.is_lead_activity ? 'Lead Activity Details' : 'Event Details'}
            </SheetTitle>
            <SheetDescription>
              {selectedEvent?.is_lead_activity 
                ? 'View-only lead activity - Edit from lead details page'
                : 'View and edit event information'}
            </SheetDescription>
          </SheetHeader>

          {selectedEvent && (
            <div className="space-y-4 mt-6">
              <div className="flex items-center gap-2">
                <Badge style={{ backgroundColor: eventTypeColors[selectedEvent.event_type] }}>
                  {eventTypeLabels[selectedEvent.event_type]}
                </Badge>
                {!selectedEvent.is_lead_activity && (
                  <Badge variant={selectedEvent.priority === 'high' ? 'destructive' : 'secondary'}>
                    {selectedEvent.priority}
                  </Badge>
                )}
              </div>

              {/* Lead Activity Info */}
              {selectedEvent.is_lead_activity && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                  <h4 className="font-semibold text-sm text-blue-900">Lead Information</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Name:</span> {selectedEvent.lead_name}</p>
                    {selectedEvent.lead_phone && (
                      <p><span className="font-medium">Phone:</span> {selectedEvent.lead_phone}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => {
                      navigate(`/leads/${selectedEvent.related_to}`);
                      setDetailSheetOpen(false);
                    }}
                  >
                    View Lead Details
                  </Button>
                </div>
              )}

              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  disabled={selectedEvent.is_lead_activity}
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  disabled={selectedEvent.is_lead_activity}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start Date & Time *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.start_time}
                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                    disabled={selectedEvent.is_lead_activity}
                  />
                </div>
                <div>
                  <Label>End Date & Time *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_time}
                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                    disabled={selectedEvent.is_lead_activity}
                  />
                </div>
              </div>

              {!selectedEvent.is_lead_activity && (
                <>
                  <div>
                    <Label>Location</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Priority</Label>
                      <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                          <SelectItem value="missed">Missed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Reminder</Label>
                    <Select
                      value={formData.reminder_minutes?.toString() || 'none'}
                      onValueChange={(value) => setFormData({...formData, reminder_minutes: value === 'none' ? null : value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No reminder</SelectItem>
                        <SelectItem value="0">At time of event</SelectItem>
                        <SelectItem value="15">15 minutes before</SelectItem>
                        <SelectItem value="60">1 hour before</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="flex justify-between gap-2 pt-4 border-t">
                {!selectedEvent.is_lead_activity && (
                  <Button
                    variant="destructive"
                    onClick={handleDeleteEvent}
                    disabled={!['pending', 'upcoming', 'scheduled'].includes(selectedEvent.status)}
                  >
                    Delete
                  </Button>
                )}
                <div className="flex gap-2 ml-auto">
                  <Button variant="outline" onClick={() => setDetailSheetOpen(false)}>
                    Close
                  </Button>
                  {!selectedEvent.is_lead_activity && (
                    <Button onClick={handleUpdateEvent}>
                      Save Changes
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
